import React from 'react';
import pinkSrc from '../assets/key-tag-pink.svg';
import greenSrc from '../assets/key-tag-green.svg';

export type KeyTagVariant = 'pink' | 'green';
type KeyTagSize = 'xl' | 'md' | 'sm';

interface KeyTagProps {
  number?: number;
  courtNumber?: number; // alias
  variant?: KeyTagVariant;
  size?: KeyTagSize;
  hero?: boolean;  // treated same as size="xl"
  label?: string;
}

const SIZES: Record<KeyTagSize, { width: number; height: number }> = {
  xl: { width: 160, height: 245 },
  md: { width: 80,  height: 122 },
  sm: { width: 52,  height: 80  },
};

// Number overlay settings per size — coordinates in SVG viewBox space (0 0 1024 1568)
const NUMBER_Y: Record<KeyTagSize, number> = {
  xl: 161,
  md: 161,
  sm: 161,
};

const NUMBER_FONT: Record<KeyTagSize, number> = {
  xl: 26,
  md: 26,
  sm: 26,
};

export const KeyTag: React.FC<KeyTagProps> = ({
  number,
  courtNumber,
  variant = 'pink',
  size = 'md',
  hero = false,
  label,
}) => {
  const courtNum = number ?? courtNumber ?? 1;
  const effectiveSize: KeyTagSize = hero ? 'xl' : (size as KeyTagSize);
  const { width, height } = SIZES[effectiveSize] ?? SIZES['md'];
  const src = variant === 'pink' ? pinkSrc : greenSrc;
  const numStr = String(courtNum).padStart(2, '0');
  const ariaLabel = label ?? `Court ${courtNum} key tag`;

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      style={{ position: 'relative', width, height, flexShrink: 0 }}
    >
      <img
        src={src}
        alt=""
        aria-hidden="true"
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
      />
      {/* Always overlay the court number — the SVG artwork no longer has a baked-in "01" */}
      <svg
        viewBox="0 0 144 216"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        aria-hidden="true"
      >
        <rect
          x="18"
          y="144"
          width="108"
          height="34"
          fill="#D94878"
          rx="0"
        />
        <text
          x="72"
          y={NUMBER_Y[effectiveSize]}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#FFF8F0"
          fontSize={NUMBER_FONT[effectiveSize]}
          fontFamily="'DM Sans', Arial, sans-serif"
          fontWeight="700"
        >
          {numStr}
        </text>
      </svg>
    </div>
  );
};

export default KeyTag;
